import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Linkedin } from "lucide-react";
import { Logo } from "./Logo";

const NAV = [
  { to: "/who-we-are", label: "Who We Are", id: "nav-who-we-are" },
  { to: "/compliance-consulting", label: "Compliance", id: "nav-compliance" },
  { to: "/capital-markets-consulting", label: "Capital Markets", id: "nav-capital-markets" },
  { to: "/trust-services-consulting", label: "Trust Services", id: "nav-trust-services" },
  { to: "/expert-witness", label: "Expert Witness", id: "nav-expert-witness" },
  { to: "/aml-independent-audits", label: "AML Audits", id: "nav-aml-audits" },
];

export const Layout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isLP = pathname.startsWith("/lp/");

  useEffect(() => setOpen(false), [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex flex-col">
      {isLP && (
        <header className="sticky top-0 z-50 bg-[#0A2B3D] border-b border-white/10" data-testid="lp-header">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 h-20 flex items-center">
            <Logo variant="light" />
          </div>
        </header>
      )}
      {!isLP && (
      <header className="sticky top-0 z-50 bg-[#0A2B3D]/95 backdrop-blur-md border-b border-white/10" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 h-20 flex items-center justify-between">
          <Logo variant="light" />
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.id}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-[0.08em] uppercase transition-colors duration-300 ${
                    isActive ? "text-[#3E9BDC]" : "text-white/80 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="nav-contact-button" className="btn-gold !py-2.5 !px-5 text-xs">
              Contact Us
            </Link>
          </nav>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>
      )}

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-[60] bg-[#082838]/70 backdrop-blur-sm lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              data-testid="mobile-menu-backdrop"
            />
            <motion.nav
              className="fixed top-0 right-0 bottom-0 z-[61] w-[84%] max-w-sm bg-[#082838] border-l border-white/10 lg:hidden flex flex-col shadow-2xl"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              data-testid="mobile-nav"
            >
              <div className="flex items-center justify-between h-20 px-6 border-b border-white/10 shrink-0">
                <span className="eyebrow text-[#A9CFE2]">Menu</span>
                <button
                  onClick={() => setOpen(false)}
                  className="text-white p-3 -mr-3"
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                >
                  <X size={26} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {NAV.map((item, i) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.05, duration: 0.3 }}
                  >
                    <NavLink
                      to={item.to}
                      data-testid={`${item.id}-mobile`}
                      className={({ isActive }) =>
                        `block py-4 min-h-[52px] text-base font-medium tracking-[0.06em] uppercase border-b border-white/10 ${
                          isActive ? "text-[#3E9BDC]" : "text-white/85"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </motion.div>
                ))}
                <Link to="/contact" data-testid="nav-contact-button-mobile" className="btn-gold mt-6 w-full justify-center !py-4">
                  Contact Us
                </Link>
              </div>
              <div className="px-6 py-6 border-t border-white/10 text-xs text-white/50 leading-relaxed shrink-0" data-testid="mobile-menu-footer">
                104 Congress Street, Suite 404 · Portsmouth, NH
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>

      <main className="flex-1">{children}</main>

      {isLP ? (
        <footer className="bg-[#082838] text-white" data-testid="lp-footer">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 py-10 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div>
              <Logo variant="light" />
              <p className="mt-4 text-xs text-white/60 leading-relaxed" data-testid="lp-footer-address">104 Congress Street, Suite 404 · Portsmouth, NH 03801</p>
            </div>
            <div className="flex flex-col sm:items-end gap-2 text-xs text-white/50">
              <span data-testid="lp-footer-copyright">Copyright © 2026 · American Elm Advisors</span>
              <span className="flex gap-5">
                <Link to="/privacy-policy" data-testid="lp-footer-privacy-link" className="hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms-conditions" data-testid="lp-footer-terms-link" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
              </span>
            </div>
          </div>
        </footer>
      ) : (
      <footer className="bg-[#082838] text-white" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <Logo variant="light" />
            <p className="mt-6 text-sm text-white/70 leading-relaxed" data-testid="footer-address">
              104 Congress Street, Suite 404
              <br />
              Portsmouth, NH 03801
            </p>
            <a
              href="https://www.linkedin.com/company/americanelm/"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-linkedin-link"
              className="mt-5 inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#3E9BDC] transition-colors"
            >
              <Linkedin size={16} /> Follow us on LinkedIn
            </a>
          </div>
          <div className="md:col-span-3">
            <p className="eyebrow text-[#A9CFE2] mb-5">Practices</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/compliance-consulting" data-testid="footer-compliance-link" className="text-white/75 hover:text-white transition-colors">Compliance Consulting</Link></li>
              <li><Link to="/capital-markets-consulting" data-testid="footer-capital-markets-link" className="text-white/75 hover:text-white transition-colors">Capital Markets Consulting</Link></li>
              <li><Link to="/trust-services-consulting" data-testid="footer-trust-services-link" className="text-white/75 hover:text-white transition-colors">Trust Services Consulting</Link></li>
              <li><Link to="/expert-witness" data-testid="footer-expert-witness-link" className="text-white/75 hover:text-white transition-colors">Expert Witness Services</Link></li>
              <li><Link to="/aml-independent-audits" data-testid="footer-aml-audits-link" className="text-white/75 hover:text-white transition-colors">AML Independent Audits</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="eyebrow text-[#A9CFE2] mb-5">Resources</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/compliance-faq" data-testid="footer-compliance-faq-link" className="text-white/75 hover:text-white transition-colors">Compliance FAQs</Link></li>
              <li><Link to="/capital-markets-faq" data-testid="footer-capital-markets-faq-link" className="text-white/75 hover:text-white transition-colors">Capital Markets FAQs</Link></li>
              <li><Link to="/trust-services-faq" data-testid="footer-trust-services-faq-link" className="text-white/75 hover:text-white transition-colors">Trust Services FAQs</Link></li>
              <li><Link to="/expert-witness-faq" data-testid="footer-expert-witness-faq-link" className="text-white/75 hover:text-white transition-colors">Expert Witness FAQs</Link></li>
              <li><Link to="/aml-independent-audits-faq" data-testid="footer-aml-audits-faq-link" className="text-white/75 hover:text-white transition-colors">AML Independent Audit FAQs</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="eyebrow text-[#A9CFE2] mb-5">Firm</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/who-we-are" data-testid="footer-who-we-are-link" className="text-white/75 hover:text-white transition-colors">Who We Are</Link></li>
              <li><Link to="/contact" data-testid="footer-contact-link" className="text-white/75 hover:text-white transition-colors">Contact Us</Link></li>
            </ul>
            <p className="eyebrow text-[#A9CFE2] mb-5 mt-8">Affiliates</p>
            <ul className="space-y-3 text-sm">
              <li><a href="https://americanelmdp.com/" target="_blank" rel="noreferrer" data-testid="footer-affiliate-dp" className="text-white/75 hover:text-white transition-colors">American Elm Distribution Partners</a></li>
              <li className="pl-4 border-l border-white/15"><a href="https://www.iamsdistributors.com" target="_blank" rel="noreferrer" data-testid="footer-affiliate-iams" className="text-white/60 hover:text-white transition-colors text-[13px]">IAMS Distributors</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/50">
            <span data-testid="footer-copyright">Copyright © 2026 · American Elm Advisors</span>
            <span className="flex gap-6">
              <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms-conditions" data-testid="footer-terms-link" className="hover:text-white transition-colors">Terms & Conditions</Link>
              <Link to="/admin/login" data-testid="footer-team-login-link" className="hover:text-white transition-colors">Team Sign-In</Link>
            </span>
          </div>
        </div>
      </footer>
      )}
    </div>
  );
};
